import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Stack, Tooltip, Typography } from '@mui/material';

import { countUploadableRows, formatUploadSummary } from './translationOverviewUtils';
import type { TranslationOverviewUploadCounts } from './types';

interface Props {
  counts: TranslationOverviewUploadCounts;
  /**
   * Whether the row may be uploaded at all. A partially translated catalog is never sent, so its
   * waiting rows are reported as blocked rather than as work the upload button will pick up.
   */
  isUploadable: boolean;
  blockedReason: string;
}

/** What a row still owes Braze: how much is waiting, how much failed, how much is already there. */
export default function UploadStatusCell({ counts, isUploadable, blockedReason }: Props) {
  const waiting = countUploadableRows(counts);

  const tooltip = (
    <Stack spacing={0.25}>
      <Typography variant="caption">In Braze: {counts.uploadedCount.toLocaleString()}</Typography>
      <Typography variant="caption">Waiting to upload: {counts.pendingUploadCount.toLocaleString()}</Typography>
      <Typography variant="caption">Failed: {counts.uploadFailedCount.toLocaleString()}</Typography>
      {!isUploadable && <Typography variant="caption">{blockedReason}</Typography>}
    </Stack>
  );

  if (!isUploadable) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        <Typography variant="body2" color="text.secondary" noWrap>
          Blocked
        </Typography>
      </Tooltip>
    );
  }

  if (counts.uploadFailedCount > 0) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        <Stack direction="row" spacing={0.5} alignItems="center" color="error.main">
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600} noWrap>
            {counts.uploadFailedCount.toLocaleString()} failed
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  if (waiting > 0) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        <Stack direction="row" spacing={0.5} alignItems="center" color="warning.main">
          <CloudQueueIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600} noWrap>
            {formatUploadSummary(counts)}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="top" arrow>
      <Stack direction="row" spacing={0.5} alignItems="center" color="success.main">
        <CheckCircleOutlineIcon fontSize="small" />
        <Typography variant="body2" fontWeight={600} noWrap>
          {formatUploadSummary(counts)}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
